import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { productService } from '../services/api';
import { useCategories } from '../hooks/useCategories';
import { useAuth } from '../hooks/useAuth';
import { ACTIVE_THEME, getThemeVars } from '../themes/dashboardThemes';
import DashboardSidebar from '../components/DashboardSidebar';
import ImageCropper from '../components/ImageCropper';
import {
  compressImage,
  validateImageFile,
  formatFileSize,
  readFile,
  analyzeImageAspectRatio,
  detectEdgeColors,
  padImageToSquare,
} from '../utils/imageUtils';

const STEPS_KEYS = ['Información básica', 'Detalles e imágenes', 'Revisión y publicación'];

const CONDITIONS = [
  { value: 'NEW', label: 'Nuevo', description: 'Artículo nuevo, sin usar', icon: 'fiber_new' },
  { value: 'LIKE_NEW', label: 'Como Nuevo', description: 'Apenas usado, excelente condición', icon: 'grade' },
  { value: 'USED', label: 'Usado', description: 'Usado pero funcional', icon: 'recycling' },
];

// ── Toast ────────────────────────────────────────────────────────────────────

function Toast({ message, type, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed dashboard-floating-bottom left-1/2 -translate-x-1/2 z-[100] animate-fade-in">
      <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-bold max-w-md ${type === 'error' ? 'bg-error text-on-error' : type === 'warning' ? 'bg-secondary text-on-secondary' : 'bg-primary text-on-primary'}`}>
        <span className="material-symbols-outlined text-base">
          {type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'check_circle'}
        </span>
        <span className="flex-1">{message}</span>
        <a onClick={onClose} className="ml-2 cursor-pointer opacity-70 hover:opacity-100 shrink-0">
          <span className="material-symbols-outlined text-base">close</span>
        </a>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

function CreateProductNew({ isModal = false, initialImages = [], onClose } = {}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const theme = getThemeVars(ACTIVE_THEME);

  const [activeStep, setActiveStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });

  const [formData, setFormData] = useState({
    name: '', description: '', price: '', condition: 'USED',
    categoryId: '', images: [],
    stock: '1', unlimitedStock: false,
  });
  const [errors, setErrors] = useState({});
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageAnalysis, setImageAnalysis] = useState([]);
  const { categories } = useCategories();

  // Category dropdown (custom — avoids iOS/Android native picker breaking fixed-height shell)
  const [categoryOpen, setCategoryOpen] = useState(false);
  const categoryRef = useRef(null);
  useEffect(() => {
    if (!categoryOpen) return;
    const onDocMouseDown = (e) => { if (categoryRef.current && !categoryRef.current.contains(e.target)) setCategoryOpen(false); };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('touchstart', onDocMouseDown);
    return () => { document.removeEventListener('mousedown', onDocMouseDown); document.removeEventListener('touchstart', onDocMouseDown); };
  }, [categoryOpen]);

  // Cropper
  const [cropperOpen, setCropperOpen] = useState(false);
  const [currentCropImage, setCurrentCropImage] = useState(null);
  const [currentCropFile, setCurrentCropFile] = useState(null);
  const [currentCropIndex, setCurrentCropIndex] = useState(null);

  useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);

  // Pre-fill images from drag-drop when opened as modal
  useEffect(() => {
    if (initialImages && initialImages.length > 0) {
      setFormData(prev => ({ ...prev, images: initialImages }));
      const previews = [];
      initialImages.forEach((file) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          previews.push({ url: reader.result, file, name: file.name, originalSize: file.size, compressedSize: file.size });
          if (previews.length === initialImages.length) {
            setImagePreviews(previews);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  }, [initialImages]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: 'success' }), 5000);
  };

  // ── Input handlers ─────────────────────────────────────────────────────

  const handleField = (field) => (e) => {
    setFormData(p => ({ ...p, [field]: e.target.value }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
  };

  const handlePrice = (e) => {
    const v = e.target.value.replace(/[^0-9.]/g, '');
    setFormData(p => ({ ...p, price: v }));
    if (v && !isNaN(parseFloat(v)) && parseFloat(v) > 999999999) {
      setErrors(p => ({ ...p, price: t('El precio no puede superar 999.999.999') }));
    } else if (errors.price) {
      setErrors(p => ({ ...p, price: '' }));
    }
  };

  const handleStock = (e) => {
    const v = e.target.value.replace(/[^0-9]/g, '');
    setFormData(p => ({ ...p, stock: v }));
    if (v && parseInt(v, 10) > 1000000) {
      setErrors(p => ({ ...p, stock: t('El stock no puede superar 1.000.000') }));
    } else if (errors.stock) {
      setErrors(p => ({ ...p, stock: '' }));
    }
  };

  const toggleUnlimitedStock = () => {
    setFormData(p => ({ ...p, unlimitedStock: !p.unlimitedStock }));
    if (errors.stock) setErrors(p => ({ ...p, stock: '' }));
  };

  // ── Image handlers ─────────────────────────────────────────────────────

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (imagePreviews.length + files.length > 5) {
      showToast(t('Se permiten máximo 5 imágenes'), 'error');
      return;
    }
    setUploadingImage(true);

    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) { showToast(validation.error, 'error'); continue; }

      try {
        const imageBase64 = await readFile(file);
        // Abrir el cropper SIEMPRE para la primera imagen (la principal) o si
        // el archivo supera 2MB. La principal define cómo se ve la card de producto,
        // así que el usuario tiene que poder elegir cómo se va a mostrar (cuadrar,
        // autoextender o dejar libre).
        const isFirstImage = imagePreviews.length === 0;
        if (isFirstImage || file.size > 2 * 1024 * 1024) {
          setCurrentCropImage(imageBase64);
          setCurrentCropFile(file);
          setCropperOpen(true);
          setUploadingImage(false);
          return;
        }

        setFormData(p => ({ ...p, images: [...p.images, file] }));

        const reader = new FileReader();
        reader.onloadend = async () => {
          const imageUrl = reader.result;
          let aspectAnalysis = { isSquare: true, orientation: 'square' };
          let edgeAnalysis = { edgesMatch: false };
          try {
            aspectAnalysis = await analyzeImageAspectRatio(imageUrl);
            edgeAnalysis = await detectEdgeColors(imageUrl);
            if (!aspectAnalysis.isSquare) {
              showToast(t('⚠️ La imagen "{{archivo}}" no es cuadrada. Considera recortarla.', { archivo: file.name }), 'warning');
            }
          } catch {}

          setImagePreviews(p => [...p, { url: imageUrl, file, name: file.name, originalSize: file.size, compressedSize: file.size }]);
          setImageAnalysis(p => [...p, { aspectRatio: aspectAnalysis, edgeColors: edgeAnalysis, fileName: file.name }]);
        };
        reader.readAsDataURL(file);
      } catch {
        showToast(t('No se pudo procesar {{archivo}}', { archivo: file.name }), 'error');
      }
    }
    setUploadingImage(false);
  };

  const handleCropSave = async (croppedFile) => {
    try {
      setUploadingImage(true);
      let finalFile = croppedFile;
      if (croppedFile.size > 1 * 1024 * 1024) {
        finalFile = await compressImage(croppedFile, { maxSizeMB: 1, maxWidthOrHeight: 1920 });
        showToast(t('Imagen comprimida a {{tamano}}', { tamano: formatFileSize(finalFile.size) }));
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const imageUrl = reader.result;
        const aspectAnalysis = await analyzeImageAspectRatio(imageUrl);
        const edgeAnalysis = await detectEdgeColors(imageUrl);

        if (currentCropIndex !== null) {
          setImagePreviews(p => { const n = [...p]; n[currentCropIndex] = { url: imageUrl, file: finalFile, name: finalFile.name, originalSize: currentCropFile?.size, compressedSize: finalFile.size }; return n; });
          setImageAnalysis(p => { const n = [...p]; n[currentCropIndex] = { aspectRatio: aspectAnalysis, edgeColors: edgeAnalysis, fileName: finalFile.name }; return n; });
          setFormData(p => { const n = [...p.images]; n[currentCropIndex] = finalFile; return { ...p, images: n }; });
        } else {
          setImagePreviews(p => [...p, { url: imageUrl, file: finalFile, name: finalFile.name, originalSize: currentCropFile?.size, compressedSize: finalFile.size }]);
          setImageAnalysis(p => [...p, { aspectRatio: aspectAnalysis, edgeColors: edgeAnalysis, fileName: finalFile.name }]);
          setFormData(p => ({ ...p, images: [...p.images, finalFile] }));
        }
      };
      reader.readAsDataURL(finalFile);
      setCropperOpen(false);
      setCurrentCropImage(null);
      setCurrentCropFile(null);
      setCurrentCropIndex(null);
    } catch {
      showToast(t('No se pudo guardar la imagen recortada'), 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setImagePreviews(p => p.filter((_, i) => i !== index));
    setImageAnalysis(p => p.filter((_, i) => i !== index));
    setFormData(p => ({ ...p, images: p.images.filter((_, i) => i !== index) }));
  };

  const handleAutoSquare = async (index) => {
    try {
      const preview = imagePreviews[index];
      const analysis = imageAnalysis[index];
      if (!preview || !analysis) return;
      setUploadingImage(true);
      const backgroundColor = analysis?.edgeColors?.edgesMatch ? analysis.edgeColors.hex : '#ffffff';
      const originalType = preview.file.type || 'image/jpeg';
      const squaredBlob = await padImageToSquare(preview.url, backgroundColor, originalType);
      const squaredFile = new File([squaredBlob], preview.file.name, { type: originalType, lastModified: Date.now() });

      const reader = new FileReader();
      reader.onloadend = async () => {
        const imageUrl = reader.result;
        const newAspect = await analyzeImageAspectRatio(imageUrl);
        const newEdge = await detectEdgeColors(imageUrl);
        setImagePreviews(p => { const n = [...p]; n[index] = { ...n[index], url: imageUrl, file: squaredFile, compressedSize: squaredFile.size }; return n; });
        setImageAnalysis(p => { const n = [...p]; n[index] = { aspectRatio: newAspect, edgeColors: newEdge, fileName: squaredFile.name }; return n; });
        setFormData(p => { const n = [...p.images]; n[index] = squaredFile; return { ...p, images: n }; });
        showToast(t('Imagen ajustada a formato cuadrado con éxito'));
      };
      reader.readAsDataURL(squaredFile);
    } catch {
      showToast(t('No se pudo ajustar la imagen automáticamente'), 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  // ── Validation ─────────────────────────────────────────────────────────

  const validateStep = (step) => {
    const e = {};
    if (step === 0) {
      if (!formData.name.trim()) e.name = t('El nombre del producto es obligatorio');
      else if (formData.name.length < 3) e.name = t('El nombre del producto debe tener al menos 3 caracteres');
      if (!formData.price) e.price = t('El precio es obligatorio');
      else if (parseFloat(formData.price) <= 0) e.price = t('El precio debe ser mayor que 0');
      else if (parseFloat(formData.price) > 999999999) e.price = t('El precio no puede superar 999.999.999');
      if (!formData.categoryId) e.categoryId = t('La categoría es obligatoria');
      if (!formData.unlimitedStock) {
        const stockNum = parseInt(formData.stock, 10);
        if (!formData.stock || isNaN(stockNum) || stockNum < 1) {
          e.stock = t('El stock debe ser mayor o igual a 1');
        } else if (stockNum > 1000000) {
          e.stock = t('El stock no puede superar 1.000.000');
        }
      }
    }
    if (step === 1) {
      if (!formData.description.trim()) e.description = t('La descripción es obligatoria');
      else if (formData.description.length < 20) e.description = t('La descripción debe tener al menos 20 caracteres');
      if (imagePreviews.length === 0) e.images = t('Debes subir al menos una imagen');
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => { if (validateStep(activeStep)) setActiveStep(p => p + 1); };
  const handleBack = () => setActiveStep(p => p - 1);

  // ── Submit ─────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validateStep(2)) return;
    setSubmitting(true);
    try {
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('price', String(Math.round(Number(formData.price))));
      submitData.append('condition', formData.condition);
      submitData.append('categoryId', String(formData.categoryId));
      // Unlimited stock: omit the field so the backend stores NULL.
      if (!formData.unlimitedStock) {
        submitData.append('stock', String(parseInt(formData.stock, 10)));
      }
      if (formData.images.length > 0) submitData.append('image', formData.images[0]);

      const response = await productService.createProductWithImages(submitData);
      const newProduct = response.data;

      if (formData.images.length > 1) {
        const results = await Promise.all(
          formData.images.slice(1).map(async (image) => {
            try { await productService.addProductImage(newProduct.id, image); return true; }
            catch { return false; }
          })
        );
        const failed = results.filter(r => !r).length;
        if (failed > 0) showToast(t('Producto creado, pero {{cantidad}} imagen(es) no se pudieron subir', { cantidad: failed }), 'warning');
        else showToast(t('¡Producto creado correctamente con todas las imágenes!'));
      } else {
        showToast(t('¡Producto creado correctamente!'));
      }

      queryClient.invalidateQueries({ queryKey: ['productCards'] });

      if (isModal && onClose) {
        setTimeout(() => { onClose(); navigate(`/products/${newProduct.id}`); }, 1500);
      } else {
        setTimeout(() => navigate(`/products/${newProduct.id}`), 1500);
      }
    } catch (err) {
      showToast(err.response?.data?.message || t('No se pudo crear el producto'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────

  const categoryName = categories.find(c => String(c.id) === String(formData.categoryId))?.name;
  const conditionLabel = CONDITIONS.find(c => c.value === formData.condition)?.label;

  // ── Render ─────────────────────────────────────────────────────────────

  const content = (
    <>
          {/* ── Card: everything inside ── */}
          <div className="bg-surface border border-outline rounded-xl overflow-hidden">

            {/* ── Header ── */}
            <div className="px-8 tablet-sm:px-10 pt-8 pb-0">
              <h1 className="text-2xl font-bold text-on-surface">{t('Crear nuevo producto')}</h1>
            </div>

            {/* ── Stepper ── */}
            <div className="flex items-center gap-3 px-8 tablet-sm:px-10 pt-6 pb-8">
              {STEPS_KEYS.map((label, i) => (
                <React.Fragment key={label}>
                  <div className="flex items-center gap-2.5">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 transition-colors ${
                      i <= activeStep ? 'bg-primary text-on-primary' : 'border-2 border-outline-variant/40 text-on-surface-variant'
                    }`}>
                      {i < activeStep ? (
                        <span className="material-symbols-outlined text-sm">check</span>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span className={`text-sm font-medium hidden tablet-sm:inline ${i === activeStep ? 'text-on-surface' : 'text-on-surface-variant/60'}`}>
                      {t(label)}
                    </span>
                  </div>
                  {i < STEPS_KEYS.length - 1 && (
                    <div className={`flex-1 h-0.5 rounded-full ${i < activeStep ? 'bg-primary' : 'bg-outline-variant/20'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="px-8 tablet-sm:hidden pb-2">
              <p className="text-sm font-semibold text-on-surface">{t(STEPS_KEYS[activeStep])}</p>
            </div>

            {/* ── Form Content ── */}
            <div className="px-8 tablet-sm:px-10 pb-2" style={{ minHeight: '400px' }}>

              {/* ═══ Step 0: Basic Info ═══ */}
              {activeStep === 0 && (
                <div className="space-y-6">
                  {/* Product Name — floating label */}
                  <div className="relative mt-2">
                    <input
                      type="text"
                      id="cp-name"
                      value={formData.name}
                      onChange={handleField('name')}
                      className={`peer w-full border-2 rounded-md px-3.5 pt-5 pb-2 text-base text-on-surface placeholder-transparent transition-colors ${errors.name ? 'border-error' : 'border-outline-variant/30 focus:border-primary'}`}
                      style={{ backgroundColor: 'white' }}
                      placeholder={t('Product Name')}
                    />
                    <label
                      htmlFor="cp-name"
                      className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors"
                    >
                      {t('Nombre del producto')} *
                    </label>
                    {errors.name && <p className="text-xs text-error mt-1.5">{errors.name}</p>}
                  </div>

                  <div className="grid grid-cols-1 tablet-sm:grid-cols-2 gap-6">
                    {/* Price — floating label */}
                    <div className="relative">
                      <div className="relative">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-variant/60 text-base">$</span>
                        <input
                          type="text"
                          id="cp-price"
                          value={formData.price}
                          onChange={handlePrice}
                          className={`peer w-full border-2 rounded-md pl-9 pr-3.5 pt-5 pb-2 text-base text-on-surface placeholder-transparent transition-colors ${errors.price ? 'border-error' : 'border-outline-variant/30 focus:border-primary'}`}
                          style={{ backgroundColor: 'white' }}
                          placeholder={t('Price')}
                        />
                        <label
                          htmlFor="cp-price"
                          className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors"
                        >
                          {t('Precio')} *
                        </label>
                      </div>
                      {errors.price && <p className="text-xs text-error mt-1.5">{errors.price}</p>}
                    </div>

                    {/* Category — custom dropdown (native select breaks the fixed-height shell on mobile) */}
                    <div className="relative" ref={categoryRef}>
                      <button
                        type="button"
                        id="cp-category"
                        aria-haspopup="listbox"
                        aria-expanded={categoryOpen}
                        onClick={() => setCategoryOpen(o => !o)}
                        className={`peer w-full border-2 rounded-md px-3.5 pt-5 pb-2 min-h-[52px] text-base text-left transition-colors cursor-pointer flex items-center justify-between ${errors.categoryId ? 'border-error' : 'border-outline-variant/30 focus:border-primary'} ${!formData.categoryId ? 'text-on-surface-variant/50' : 'text-on-surface'}`}
                        style={{ backgroundColor: 'white' }}
                      >
                        <span className="truncate">
                          {formData.categoryId
                            ? (categories.find(c => String(c.id) === String(formData.categoryId))?.name || t('Seleccioná una categoría'))
                            : t('Seleccioná una categoría')}
                        </span>
                        <span className="material-symbols-outlined text-on-surface-variant/50 text-xl shrink-0 ml-2">arrow_drop_down</span>
                      </button>
                      <label
                        htmlFor="cp-category"
                        className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors pointer-events-none"
                      >
                        {t('Categoría')} *
                      </label>
                      {categoryOpen && (
                        <div role="listbox" className="absolute top-full left-0 right-0 mt-1 bg-surface-container rounded-xl shadow-xl border border-outline-variant/20 p-1.5 z-50 max-h-[280px] overflow-y-auto">
                          {categories.map(c => {
                            const sel = String(c.id) === String(formData.categoryId);
                            return (
                              <button
                                key={c.id}
                                type="button"
                                role="option"
                                aria-selected={sel}
                                onClick={() => { setFormData(p => ({ ...p, categoryId: c.id })); setErrors(e => ({ ...e, categoryId: undefined })); setCategoryOpen(false); }}
                                className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-colors text-on-surface ${sel ? 'bg-primary-container/30 font-bold' : 'font-medium hover:bg-surface-container-high'}`}
                              >
                                {c.name}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {errors.categoryId && <p className="text-xs text-error mt-1.5">{errors.categoryId}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-base font-medium text-on-surface mb-3">{t('Condición')}</label>
                    <div className="flex rounded border border-outline-variant/30 overflow-hidden divide-x divide-outline-variant/30">
                      {CONDITIONS.map(c => (
                        <a
                          key={c.value}
                          onClick={() => setFormData(p => ({ ...p, condition: c.value }))}
                          className={`flex-1 py-6 px-3 text-center cursor-pointer transition-colors ${
                            formData.condition === c.value
                              ? 'bg-black/[0.06]'
                              : 'bg-white hover:bg-black/[0.03]'
                          }`}
                        >
                          <p className={`text-sm uppercase tracking-wide ${formData.condition === c.value ? 'font-bold text-on-surface' : 'font-normal text-on-surface-variant'}`}>{t(c.label)}</p>
                          <p className={`text-xs uppercase tracking-wide mt-1.5 ${formData.condition === c.value ? 'text-on-surface/70' : 'text-on-surface-variant/50'}`}>{t(c.description)}</p>
                        </a>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-base font-medium text-on-surface mb-3">{t('Disponibilidad')}</label>
                    <div className="flex flex-col mobile-sm:flex-row gap-3 items-stretch mobile-sm:items-start">
                      {/* Quantity input */}
                      <div className={`flex-1 transition-opacity ${formData.unlimitedStock ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                        <div className="relative">
                          <input
                            type="text"
                            inputMode="numeric"
                            id="cp-stock"
                            value={formData.unlimitedStock ? '' : formData.stock}
                            onChange={handleStock}
                            disabled={formData.unlimitedStock}
                            className={`peer w-full border-2 rounded-md px-3.5 pt-5 pb-2 text-base text-on-surface placeholder-transparent transition-colors ${errors.stock ? 'border-error' : 'border-outline-variant/30 focus:border-primary'}`}
                            style={{ backgroundColor: 'white' }}
                            placeholder={t('Stock')}
                          />
                          <label
                            htmlFor="cp-stock"
                            className="absolute left-3 -top-2.5 bg-white px-1 text-sm text-on-surface-variant peer-focus:text-primary transition-colors"
                          >
                            {t('Cantidad disponible')} *
                          </label>
                        </div>
                        {errors.stock && !formData.unlimitedStock && (
                          <p className="text-xs text-error mt-1.5">{errors.stock}</p>
                        )}
                      </div>

                      {/* Unlimited toggle pill */}
                      <a
                        onClick={toggleUnlimitedStock}
                        role="button"
                        aria-pressed={formData.unlimitedStock}
                        className={`flex items-center justify-center gap-2 px-4 mobile-sm:px-5 py-3 rounded-md border-2 cursor-pointer transition-all select-none whitespace-nowrap ${
                          formData.unlimitedStock
                            ? 'border-primary bg-primary text-on-primary shadow-sm'
                            : 'border-outline-variant/30 bg-white text-on-surface-variant hover:border-primary/40 hover:text-on-surface'
                        }`}
                      >
                        <span className="material-symbols-outlined text-[20px]">all_inclusive</span>
                        <span className="text-sm font-semibold">{t('Stock ilimitado')}</span>
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* ═══ Step 1: Details & Images ═══ */}
              {activeStep === 1 && (
                <div className="space-y-7">
                  <div>
                    <label className="block text-base font-medium text-on-surface mb-2">{t('Descripción')} *</label>
                    <textarea
                      value={formData.description}
                      onChange={handleField('description')}
                      rows={4}
                      className={`w-full border rounded-lg px-4 py-3 text-base text-on-surface placeholder:text-on-surface-variant/40 focus:outline-none focus:ring-2 focus:ring-primary/25 transition-shadow resize-none ${errors.description ? 'border-error focus:ring-error/25' : 'border-outline-variant/40 focus:border-primary'}`}
                      style={{ backgroundColor: 'white' }}
                      placeholder={t('Describe tu producto en detalle...')}
                    />
                    {errors.description && <p className="text-sm text-error mt-1.5">{errors.description}</p>}
                  </div>

                  {/* Images */}
                  <div>
                    <label className="block text-base font-medium text-on-surface mb-2">{t('Imágenes del producto')} *</label>

                    <div className="flex items-start gap-2 p-3.5 rounded-lg bg-primary/5 border border-primary/10 mb-4">
                      <span className="material-symbols-outlined text-primary text-lg mt-0.5">info</span>
                      <div>
                        <p className="text-sm font-medium text-on-surface mb-1">{t('Consejos para una mejor visualización:')}</p>
                        <ul className="text-sm text-on-surface-variant list-disc pl-4 space-y-0.5">
                          <li>{t('Las imágenes cuadradas (relación 1:1) se ven mejor en las tarjetas')}</li>
                          <li>{t('Detectaremos imágenes no cuadradas y ofreceremos ajuste automático')}</li>
                          <li>{t('Máximo 5 imágenes, de hasta 5MB cada una')}</li>
                        </ul>
                      </div>
                    </div>

                    {errors.images && (
                      <div className="flex items-center gap-2 p-3.5 rounded-lg bg-error/10 border border-error/20 mb-4">
                        <span className="material-symbols-outlined text-error text-lg">error</span>
                        <p className="text-sm font-semibold text-error">{errors.images}</p>
                      </div>
                    )}

                    <div className="flex gap-3 flex-wrap">
                      {imagePreviews.map((preview, index) => {
                        const analysis = imageAnalysis[index];
                        const isSquare = analysis?.aspectRatio?.isSquare;
                        const canAutoSquare = !isSquare && analysis?.edgeColors?.edgesMatch;

                        return (
                          <div key={preview.name} className="relative group w-[140px]">
                            <div className="w-[140px] h-[120px] rounded-lg overflow-hidden bg-surface-container-highest relative border border-outline-variant/20">
                              <img src={preview.url} alt="" className="w-full h-full object-cover" />
                              {index === 0 && (
                                <span className="absolute top-1.5 left-1.5 px-2 py-0.5 rounded-full bg-primary text-on-primary text-[9px] font-bold uppercase">{t('Principal')}</span>
                              )}
                              {analysis && (
                                <span className="absolute bottom-1.5 left-1.5" title={isSquare ? t('Imagen cuadrada') : t('Imagen {{orientacion}}', { orientacion: analysis.aspectRatio?.orientation })}>
                                  <span className={`material-symbols-outlined text-base ${isSquare ? 'text-primary' : 'text-secondary'}`} style={{ fontVariationSettings: "'FILL' 1" }}>
                                    {isSquare ? 'check_circle' : 'warning'}
                                  </span>
                                </span>
                              )}
                              <div className="absolute top-1.5 right-1.5 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {canAutoSquare && (
                                  <a onClick={() => handleAutoSquare(index)} className="w-7 h-7 rounded-md bg-white/90 flex items-center justify-center cursor-pointer hover:bg-primary hover:text-on-primary transition-colors shadow-sm" title={t('Ajustar automáticamente usando color de borde')}>
                                    <span className="material-symbols-outlined text-sm">crop_free</span>
                                  </a>
                                )}
                                <a onClick={async () => { const b64 = await readFile(preview.file); setCurrentCropImage(b64); setCurrentCropFile(preview.file); setCurrentCropIndex(index); setCropperOpen(true); }} className="w-7 h-7 rounded-md bg-white/90 flex items-center justify-center cursor-pointer hover:bg-primary hover:text-on-primary transition-colors shadow-sm" title={t('Recortar imagen')}>
                                  <span className="material-symbols-outlined text-sm">crop</span>
                                </a>
                                <a onClick={() => removeImage(index)} className="w-7 h-7 rounded-md bg-white/90 flex items-center justify-center cursor-pointer hover:bg-error hover:text-on-error transition-colors shadow-sm" title={t('Eliminar imagen')}>
                                  <span className="material-symbols-outlined text-sm">delete</span>
                                </a>
                              </div>
                            </div>
                            <p className="text-xs text-on-surface-variant mt-1.5 truncate">{preview.name}</p>
                            <p className="text-xs text-on-surface-variant/50">{formatFileSize(preview.compressedSize || preview.file.size)}</p>
                          </div>
                        );
                      })}

                      {imagePreviews.length < 5 && (
                        <label className={`w-[140px] h-[120px] rounded-lg border-2 border-dashed border-outline-variant/30 flex flex-col items-center justify-center cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-colors ${uploadingImage ? 'opacity-50 pointer-events-none' : ''}`}>
                          {uploadingImage ? (
                            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <span className="material-symbols-outlined text-2xl text-on-surface-variant/50 mb-1">add_photo_alternate</span>
                              <span className="text-xs font-medium text-on-surface-variant/50">{t('Agregar foto')}</span>
                            </>
                          )}
                          <input type="file" className="hidden" accept="image/*" multiple onChange={handleImageUpload} />
                        </label>
                      )}
                    </div>
                    <p className="text-xs text-on-surface-variant/50 mt-2">{t('Sube hasta 5 imágenes (máximo 5MB cada una)')}</p>
                  </div>

                </div>
              )}

              {/* ═══ Step 2: Review ═══ */}
              {activeStep === 2 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-on-surface">{t('Resumen del producto')}</h3>

                  <div className="border border-outline-variant/20 rounded-lg overflow-hidden">
                    <div className="grid grid-cols-1 tablet-sm:grid-cols-2 gap-x-8 gap-y-5 p-6">
                      <div>
                        <span className="text-sm text-on-surface-variant">{t('Nombre')}</span>
                        <p className="text-base font-medium text-on-surface mt-0.5">{formData.name || '—'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-on-surface-variant">{t('Precio')}</span>
                        <p className="text-base font-medium text-on-surface mt-0.5">${formData.price || '0'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-on-surface-variant">{t('Categoría')}</span>
                        <p className="text-base font-medium text-on-surface mt-0.5">{categoryName || '—'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-on-surface-variant">{t('Condición')}</span>
                        <p className="text-base font-medium text-on-surface mt-0.5">{conditionLabel ? t(conditionLabel) : '—'}</p>
                      </div>
                      <div>
                        <span className="text-sm text-on-surface-variant">{t('Disponibilidad')}</span>
                        <p className="text-base font-medium text-on-surface mt-0.5">
                          {formData.unlimitedStock ? t('Stock ilimitado') : (formData.stock || '—')}
                        </p>
                      </div>
                      <div className="col-span-1 tablet-sm:col-span-2">
                        <span className="text-sm text-on-surface-variant">{t('Descripción')}</span>
                        <p className="text-base text-on-surface mt-1 whitespace-pre-wrap leading-relaxed">{formData.description || '—'}</p>
                      </div>
                      <div className="col-span-1 tablet-sm:col-span-2">
                        <span className="text-sm text-on-surface-variant">{t('Imágenes')} ({imagePreviews.length})</span>
                        <div className="flex gap-2 mt-2">
                          {imagePreviews.map((p) => (
                            <img key={p.name} src={p.url} alt="" className="w-16 h-16 rounded-lg object-cover border border-outline-variant/20" />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 p-3.5 rounded-lg bg-primary/5 border border-primary/10">
                    <span className="material-symbols-outlined text-primary text-lg mt-0.5">info</span>
                    <p className="text-sm text-on-surface-variant">{t('Al publicar este producto, aceptas nuestros términos de servicio y las normas de la plataforma.')} {t('Tu producto será visible en todas tus comunidades.')}</p>
                  </div>
                </div>
              )}
            </div>

            {/* ── Footer Actions ── */}
            <div className="flex flex-col-reverse mobile-sm:flex-row mobile-sm:items-center mobile-sm:justify-between gap-3 px-8 tablet-sm:px-10 py-5 mt-6 border-t border-outline-variant/15">
              <a
                onClick={activeStep > 0 ? handleBack : undefined}
                className={`w-full mobile-sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  activeStep > 0 ? 'text-on-surface-variant cursor-pointer hover:bg-surface-container' : 'text-on-surface-variant/30 pointer-events-none'
                }`}
              >
                <span className="material-symbols-outlined text-lg">arrow_back</span>
                {t('Atrás')}
              </a>

              <div className="flex flex-col mobile-sm:flex-row items-center gap-3 w-full mobile-sm:w-auto">
                <a
                  onClick={() => isModal && onClose ? onClose() : navigate(-1)}
                  className="w-full mobile-sm:w-auto px-5 py-2.5 rounded-lg text-center text-sm font-medium text-on-surface-variant cursor-pointer hover:bg-surface-container border border-outline-variant/30 transition-colors"
                >
                  {t('Cancelar')}
                </a>

                {activeStep === STEPS_KEYS.length - 1 ? (
                  <a
                    onClick={!submitting ? handleSubmit : undefined}
                    className={`w-full mobile-sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-primary text-on-primary rounded-lg text-sm font-semibold shadow-sm transition-all ${
                      submitting ? 'opacity-50 pointer-events-none' : 'cursor-pointer hover:opacity-90 active:scale-[0.98]'
                    }`}
                  >
                    {submitting ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <span className="material-symbols-outlined text-lg">check</span>
                    )}
                    {submitting ? t('Publicando...') : t('Publicar producto')}
                  </a>
                ) : (
                  <a
                    onClick={handleNext}
                    className="w-full mobile-sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-primary text-on-primary rounded-lg text-sm font-semibold cursor-pointer hover:opacity-90 active:scale-[0.98] transition-all"
                  >
                    {t('Siguiente')}
                    <span className="material-symbols-outlined text-lg">arrow_forward</span>
                  </a>
                )}
              </div>
            </div>
          </div>

      {/* Image Cropper (still uses MUI Dialog internally) */}
      <ImageCropper
        open={cropperOpen}
        image={currentCropImage}
        onClose={() => { setCropperOpen(false); setCurrentCropImage(null); setCurrentCropFile(null); setCurrentCropIndex(null); }}
        onSave={handleCropSave}
        originalFile={currentCropFile}
      />

      <Toast message={toast.message} type={toast.type} onClose={() => setToast({ message: '', type: 'success' })} />
    </>
  );

  if (isModal) {
    return content;
  }

  return (
    <div
      className="dashboard-new font-body selection:bg-primary-container selection:text-on-primary-container h-[100dvh] overflow-hidden flex flex-col tablet-sm:flex-row"
      style={{ ...theme, backgroundColor: 'var(--chrome-bg)', color: 'var(--chrome-text)' }}
    >
      <DashboardSidebar activePath="" />
      <main className="flex-1 h-[100dvh] overflow-y-auto hide-scrollbar cm-pb-mobile-nav">
        <div className="px-6 tablet-sm:px-12 pt-8 pb-20 max-w-5xl mx-auto">
          {content}
        </div>
      </main>
    </div>
  );
}

export default CreateProductNew;
